/**
 * Schema.org JSON-LD structured data generators
 * For SEO, GEO (Generative Engine Optimization), and AEO (Answer Engine Optimization)
 */

const SITE_URL = process.env.NEXT_PUBLIC_SITE_URL || 'https://www.stock-co2.com'
const SITE_NAME = 'Stock CO2'
// /logo.png n'existe pas (404, relevé du 29/09/2026) : icône carrée 512 px du site.
const LOGO_URL = `${SITE_URL}/android-chrome-512x512.png`
/** Image de repli des JSON-LD (/og-default.jpg n'existe pas : 404). */
const IMAGE_REPLI = `${SITE_URL}/api/og?title=Stock+CO2`

/** Profils publics de Stock CO2 (sameAs) : seulement des URL vérifiées. */
export const PROFILS_STOCK_CO2 = ['https://www.linkedin.com/company/stockco2/']

// ─── Organization ───
export function organizationJsonLd() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': `${SITE_URL}/#organisation`,
    name: SITE_NAME,
    legalName: 'STOCK CO2',
    url: SITE_URL,
    logo: { '@type': 'ImageObject', url: LOGO_URL, width: 512, height: 512 },
    image: LOGO_URL,
    // Identifiant officiel (mentions légales) : relie l'entité au registre.
    identifier: { '@type': 'PropertyValue', propertyID: 'SIRET', value: '88108121000026' },
    description:
      'Stock CO2 accompagne les entreprises dans leur contribution carbone avec des projets français labellisés Label Bas-Carbone. Impact mesuré sur 5 dimensions écosystémiques.',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '3 Boulevard de Sébastopol',
      addressLocality: 'Paris',
      postalCode: '75001',
      addressCountry: 'FR',
    },
    contactPoint: {
      '@type': 'ContactPoint',
      email: 'contact@stock-co2.fr',
      contactType: 'customer service',
      availableLanguage: ['French', 'English'],
    },
    sameAs: PROFILS_STOCK_CO2,
    knowsAbout: ['Label Bas-Carbone', 'Contribution carbone', 'Compensation carbone', 'Reboisement', 'Agriculture bas-carbone', 'CSRD'],
    foundingDate: '2019',
    numberOfEmployees: {
      '@type': 'QuantitativeValue',
      minValue: 10,
      maxValue: 50,
    },
    // Le Label Bas-Carbone distingue des PROJETS, pas Stock CO2 : il n'est pas une récompense de l'entreprise.
    award: ['Certification B Corp'],
  }
}

// ─── Website (for sitelinks search box) ───
export function websiteJsonLd() {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: SITE_NAME,
    url: SITE_URL,
    description: 'Contribution carbone avec des projets français labellisés Label Bas-Carbone, à impact écosystémique mesuré.',
    publisher: { '@id': `${SITE_URL}/#organisation` },
    potentialAction: {
      '@type': 'SearchAction',
      target: `${SITE_URL}/projets?q={search_term_string}`,
      'query-input': 'required name=search_term_string',
    },
    inLanguage: ['fr', 'en'],
  }
}

// ─── Breadcrumb ───
export function breadcrumbJsonLd(items: { name: string; url: string }[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: item.name,
      item: `${SITE_URL}${item.url}`,
    })),
  }
}

// ─── FAQ Page ───
export function faqJsonLd(questions: { question: string; answer: string }[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: questions.map((q) => ({
      '@type': 'Question',
      name: q.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: q.answer,
      },
    })),
  }
}

// ─── Blog Article ───
export function articleJsonLd(article: {
  title: string
  slug: string
  excerpt: string
  publishedDate: string
  category?: string
  imageUrl?: string
  author?: {
    name?: string
    email?: string
  } | null
  updatedDate?: string
}) {
  const dateModified = article.updatedDate || article.publishedDate

  return {
    '@context': 'https://schema.org',
    '@type': 'NewsArticle',
    headline: article.title,
    description: article.excerpt,
    url: `${SITE_URL}/blog/${article.slug}`,
    datePublished: article.publishedDate,
    dateModified,
    author: article.author?.name
      ? {
          '@type': 'Person',
          name: article.author.name,
        }
      : {
          '@type': 'Organization',
          name: SITE_NAME,
          url: SITE_URL,
        },
    publisher: {
      '@type': 'Organization',
      name: SITE_NAME,
      logo: { '@type': 'ImageObject', url: LOGO_URL },
      url: SITE_URL,
    },
    image: article.imageUrl || IMAGE_REPLI,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${SITE_URL}/blog/${article.slug}`,
    },
    articleSection: article.category || 'Blog',
    inLanguage: 'fr-FR',
    keywords: article.category ? [article.category] : [],
  }
}

// ─── Project (as Service — pas de prix affiché) ───
export function projectJsonLd(project: {
  title: string
  slug: string
  presentation: string
  type: string
  region?: string
  commune?: string
  surface?: number
  co2Tonnes?: number
  imageUrl?: string
}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: project.title,
    description: project.presentation,
    url: `${SITE_URL}/projets/${project.slug}`,
    image: project.imageUrl || IMAGE_REPLI,
    provider: {
      '@type': 'Organization',
      name: SITE_NAME,
      url: SITE_URL,
    },
    serviceType: `Contribution carbone — ${project.type}`,
    areaServed: {
      '@type': 'Country',
      name: 'France',
    },
    additionalProperty: [
      ...(project.surface
        ? [{ '@type': 'PropertyValue', name: 'Surface', value: `${project.surface} ha` }]
        : []),
      ...(project.co2Tonnes
        ? [{ '@type': 'PropertyValue', name: "Réductions d'émissions attendues", value: `${project.co2Tonnes} tCO₂` }]
        : []),
      ...(project.region
        ? [{ '@type': 'PropertyValue', name: 'Région', value: project.region }]
        : []),
    ],
    ...(project.commune && project.region && {
      contentLocation: {
        '@type': 'Place',
        name: `${project.commune}, ${project.region}`,
        address: {
          '@type': 'PostalAddress',
          addressRegion: project.region,
          addressCountry: 'FR',
        },
      },
    }),
  }
}

// ─── Service (for homepage) ───
export function serviceJsonLd() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Contribution carbone à impact écosystémique',
    description:
      'Accompagnement des entreprises dans leur contribution carbone avec des projets français labellisés Label Bas-Carbone, mesurés sur 5 dimensions écosystémiques.',
    provider: {
      '@type': 'Organization',
      name: SITE_NAME,
      url: SITE_URL,
    },
    areaServed: {
      '@type': 'Country',
      name: 'France',
    },
    serviceType: 'Contribution carbone / Compensation carbone',
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'Projets carbone labellisés',
      itemListElement: [
        { '@type': 'OfferCatalog', name: 'Projets forestiers (reboisement, boisement)' },
        { '@type': 'OfferCatalog', name: 'Projets agricoles (grandes cultures, élevage)' },
      ],
    },
  }
}

// ─── Local Business (for contact/location) ───
export function localBusinessJsonLd() {
  return {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    name: SITE_NAME,
    image: LOGO_URL,
    description:
      'Contribution carbone à impact écosystémique : sélection, audit et suivi de projets français labellisés Label Bas-Carbone.',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '3 Boulevard de Sébastopol',
      addressLocality: 'Paris',
      postalCode: '75001',
      addressCountry: 'FR',
    },
    telephone: '+33 1 84 16 84 07',
    email: 'contact@stock-co2.fr',
    url: SITE_URL,
    '@id': `${SITE_URL}/#organisation`,
    sameAs: PROFILS_STOCK_CO2,
    priceRange: '€€',
    openingHoursSpecification: [
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
        opens: '09:00',
        closes: '18:00',
      },
    ],
  }
}

// ─── Event (for webinars/seminars) ───
export function eventJsonLd(event: {
  name: string
  description: string
  startDate: string
  endDate: string
  location?: string
  eventUrl?: string
  image?: string
  organizer?: string
}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Event',
    name: event.name,
    description: event.description,
    image: event.image || LOGO_URL,
    startDate: event.startDate,
    endDate: event.endDate,
    eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
    ...(event.location && {
      location: {
        '@type': 'Place',
        name: event.location,
      },
    }),
    ...(event.eventUrl && {
      url: event.eventUrl,
    }),
    organizer: {
      '@type': 'Organization',
      name: event.organizer || SITE_NAME,
      url: SITE_URL,
    },
  }
}

// ─── Helper: render JSON-LD script tag ───
export function JsonLd({ data }: { data: Record<string, any> }) {
  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }}
    />
  )
}
